import { createContext, useContext, useEffect, useState, useMemo } from "react";
import React from "react";
import PropTypes from "prop-types";
import { useAuth } from "react-oidc-context";

export const CenterContext = createContext({
  centres: [],
  selectedCentreId: "",
  selectedCenter: null,
  centerNumber: "", // added back for compatibility
  centerName: "", // added back for compatibility
  user: null,
  roles: [],
  businessStream: [],
  loading: false,
  setSelectedCentreId: () => {},
});

export const CenterProvider = ({ children }) => {
  const auth = useAuth();
  const [userInfo, setUserInfo] = useState(null);
  const [loadingUser, setLoadingUser] = useState(true);

  // Core selection state
  const [selectedCentreId, setSelectedCentreId] = useState("");

  // Derived / compatibility fields
  const [centerNumber, setCenterNumber] = useState("");
  const [centerName, setCenterName] = useState("");

  const [selectedBpid, setSelectedBpid] = useState("");
  const [roles, setRoles] = useState([]);
  const [businessStream, setBusinessStream] = useState([]);

  // 1. Fetch user info
  useEffect(() => {
    
    if (auth.isAuthenticated && auth.user?.access_token) {
      setLoadingUser(true);
      fetch(process.env.REACT_APP_IDENTITY_USERINFO_URL, {
        headers: { Authorization: `Bearer ${auth.user.access_token}` },
      })
        .then((res) => res.json())
        .then((data) => setUserInfo(data))
        .catch((err) => console.error("Error fetching userinfo", err))
        .finally(() => setLoadingUser(false));
    } else {
      setLoadingUser(false);
    }
  }, [auth.isAuthenticated, auth.user]);

  // 2. Build centres from userInfo
  const centres = useMemo(() => {
    const built = (userInfo?.orgs || [])
      .map((org) => ({
        orgId: org?.sourceSystems?.[0]?.orgId || "",
        name: org?.name || "",
        bpid: org?.bpid || "",
        productGroup: org?.productGroup || [],
      }))
      .filter((c) => c.orgId || c.bpid);

    if (built.length) {
      
    }
    return built;
  }, [userInfo]);

  // 3. Initialize selectedCentreId (once centres & userInfo are ready)
  useEffect(() => {
    if (!userInfo || centres.length === 0) return;

    // Try localStorage first
    const storedCentreIdRaw = localStorage.getItem("selectedCentreId");
    let storedCentreId = "";
    try {
      storedCentreId = storedCentreIdRaw ? JSON.parse(storedCentreIdRaw) : "";
    } catch {
      storedCentreId = storedCentreIdRaw || "";
    }

    if (storedCentreId && centres.some((c) => c.orgId === storedCentreId)) {
      setSelectedCentreId(storedCentreId);
      return;
    }

    // Fallback to lastSelectedOrgNCN if valid
    const fromLast = userInfo.lastSelectedOrgNCN;
    if (fromLast && centres.some((c) => c.orgId === fromLast)) {
      setSelectedCentreId(fromLast);
      return;
    }

    // Final fallback: first centre
    setSelectedCentreId(centres[0].orgId);
  }, [userInfo, centres]);

  // 4. Derive selectedCentre
  const selectedCenter = useMemo(
    () => centres.find((c) => c.orgId === selectedCentreId) || null,
    [centres, selectedCentreId]
  );

  // 5. Sync compatibility fields
  useEffect(() => {
    if (selectedCenter) {
      setCenterNumber(selectedCenter.orgId);
      setCenterName(selectedCenter.name);
    } else {
      setCenterNumber("");
      setCenterName("");
    }
  }, [selectedCenter]);

  // 6. Persist selectedCentreId whenever it changes (and is valid)
  useEffect(() => {
    if (selectedCentreId) {
      localStorage.setItem(
        "selectedCentreId",
        JSON.stringify(selectedCentreId)
      );
    }
  }, [selectedCentreId]);

  // (Optional) If you also want to persist name separately:
  useEffect(() => {
    if (centerName) {
      localStorage.setItem("selectedCenterName", JSON.stringify(centerName));
    }
  }, [centerName]);

  // 7. Update bpid / product group
  useEffect(() => {
    if (selectedCenter) {
      setSelectedBpid(selectedCenter.bpid);
      setBusinessStream(selectedCenter.productGroup || []);
    } else {
      setSelectedBpid("");
      setBusinessStream([]);
    }
  }, [selectedCenter]);

  // 8. Extract roles matching bpid
  useEffect(() => {
    if (!selectedBpid || !userInfo?.roles) {
      setRoles([]);
      return;
    }
    const match = userInfo.roles.find((r) => r.bpid === selectedBpid);
    setRoles(match ? match.roleNames : []);
  }, [selectedBpid, userInfo]);

  const contextValue = useMemo(
    () => ({
      centres,
      selectedCentreId,
      selectedCenter,
      centerNumber,
      centerName,
      user: userInfo,
      roles,
      businessStream,
      loading: loadingUser,
      setSelectedCentreId,
    }),
    [
      centres,
      selectedCentreId,
      selectedCenter,
      centerNumber,
      centerName,
      userInfo,
      roles,
      businessStream,
      loadingUser,
    ]
  );

  return (
    <CenterContext.Provider value={contextValue}>
      {children}
    </CenterContext.Provider>
  );
};

export const useCenter = () => useContext(CenterContext);

CenterProvider.propTypes = {
  auth: PropTypes.shape({
    isAuthenticated: PropTypes.bool.isRequired,
    user: PropTypes.shape({
      access_token: PropTypes.string,
    }),
  }).isRequired,
  children: PropTypes.node.isRequired,
};
